import { Form, SimpleItem } from "devextreme-react/ui/form";
import { useEffect, useMemo, useState, useRef } from "react";
import { userApi } from "../../../api";
import CollapseSection from "../../../components/CollaspeSection";
import { getDateFormat } from "../../../utils/date-format";
import { Template } from "devextreme-react/core/template";
import UserSelectBoxItem from "../../../components/SelectBoxItems/UserSelectBoxItem";
import TimeEntriesAPI from "../../../api/TimeEntries/time-entries.js";
import {
  applyTimeIntervalSelection,
  cloneReportInfoForForm,
  getTimeIntervalVisibility,
} from "./report-params-utils";
import {
  ALL_SELECTED_FILTERS_KEY,
  UNASSIGNED_FILTER_VALUE,
} from "./filter-constants";

const TIME_DIMENSION_ITEMS = ["Start Date", "End Date", "Invoice Date"];

const TimeEntriesParamsForm = ({ paramsForm, reportInfo }) => {
  const [editorVisiblity, setEditorVisbility] = useState({
    ...getTimeIntervalVisibility(reportInfo?.report_parameters?.time_interval),
    invoiced: true,
    categories: true,
    user: true,
  });

  let startDateRef = null;
  let endDateRef = null;

  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    userApi.getUsers({ includeObservers: false }).then(setUsers);

    TimeEntriesAPI.getTimeCategories().then(setCategories);
  }, []);

  // Option lists are memoized so the seeding effects below can set tag box
  // values with the same object identities the editors' items use (required
  // for the user object tag box, which has no valueExpr). Users has no
  // unassigned sentinel: time entries always have a user.
  const categoryOptions = useMemo(
    () => [
      { category_id: UNASSIGNED_FILTER_VALUE, category_name: "(No Category)" },
      ...categories,
    ],
    [categories]
  );

  const userOptions = useMemo(
    () =>
      users.map((cl) => ({
        user_id: cl.user_id,
        email: cl.email,
        full_name: cl.full_name,
        title: cl.title,
      })),
    [users]
  );

  // Fields saved as "all selected" reseed their tag box fully checked once
  // the lookup resolves. Values are derived from the current options, not a
  // snapshot, so options added since the save show up checked too.
  const allSelectedFilters = useMemo(
    () => reportInfo?.report_parameters?.[ALL_SELECTED_FILTERS_KEY] || [],
    [reportInfo]
  );

  const seededRef = useRef({});
  const seedAllSelected = (paramKey, values) => {
    if (seededRef.current[paramKey]) return;
    if (!allSelectedFilters.includes(paramKey)) return;
    // A sentinel bearing field carries its "__unassigned__" option
    // synchronously, before the lookup resolves, so wait for at least one real
    // (non sentinel) value before seeding. Seeding and locking on the sentinel
    // only state would strand the field on "(No ...)".
    const hasRealValue = values.some((v) =>
      v && typeof v === "object"
        ? !Object.values(v).includes(UNASSIGNED_FILTER_VALUE)
        : v !== UNASSIGNED_FILTER_VALUE
    );
    if (!hasRealValue) return;
    // Seed each all selected field once. Re-running on later option identity
    // changes (e.g. a shared query refetch) would reset the tag box to fully
    // checked and clobber a partial deselection the user made mid edit.
    seededRef.current[paramKey] = true;
    paramsForm.current?.instance?.updateData(
      `report_parameters.${paramKey}`,
      values
    );
  };

  useEffect(() => {
    seedAllSelected(
      "category",
      categoryOptions.map((c) => c.category_id)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected("users", userOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOptions, allSelectedFilters]);

  return (
    <>
      <CollapseSection
        title={"Report Parameters | Time Entries - Overview"}
        allowCollapse={false}
      >
        <Form
          ref={paramsForm}
          colCount={2}
          defaultFormData={cloneReportInfoForForm(reportInfo)}
        >
          <SimpleItem
            dataField="report_parameters.time_interval"
            label={{ text: "Time Interval", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            editorOptions={{
              items: [
                "All Time",
                "Custom",
                "Today",
                "Yesterday",
                "This Week",
                "Last Week",
                "This Month",
                "Last Month",
                "This Year",
                "Last Year",
              ],
              placeholder: "All Time",
              dropDownOptions: {
                maxHeight: 250,
              },
              onValueChanged: (e) => {
                const interval = applyTimeIntervalSelection(
                  e,
                  paramsForm.current?.instance,
                  TIME_DIMENSION_ITEMS
                );
                setEditorVisbility({
                  ...editorVisiblity,
                  ...getTimeIntervalVisibility(interval),
                });
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.timestamp_dimension"
            label={{ text: "Time Dimension", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            isRequired={true}
            visible={editorVisiblity.dimension}
            editorOptions={{
              items: TIME_DIMENSION_ITEMS,
              onValueChanged: (e) => {
                setEditorVisbility({
                  ...editorVisiblity,
                  invoiced: e.value !== "Invoice Date",
                });
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.start_date"
            label={{ text: "Start Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.startDate}
            editorOptions={{
              onInitialized: (e) => {
                startDateRef = e.component;
              },
              onValueChanged: (e) => {
                endDateRef.option("min", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />

          <SimpleItem
            dataField="report_parameters.end_date"
            label={{ text: "End Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.endDate}
            editorOptions={{
              onInitialized: (e) => {
                endDateRef = e.component;
              },
              onValueChanged: (e) => {
                startDateRef.option("max", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />

          <SimpleItem
            dataField="report_parameters.invoiced"
            label={{ text: "Invoiced", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            visible={editorVisiblity.invoiced}
            editorOptions={{
              items: ["All", "Yes", "No"],
              placeholder: "All",
              showClearButton: true,
              showDropDownButton: true,
              dropDownOptions: {
                maxHeight: 250,
              },
              onValueChanged: (e) => {
                // "All" is the blank default; selecting it reverts the box to
                // its placeholder rather than storing a value.
                if (e.value === "All") {
                  e.component.option("value", null);
                }
              },
            }}
          />

          <SimpleItem
            dataField="report_parameters.category"
            label={{ text: "Categories" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "category_id",
              displayExpr: "category_name",
              items: categoryOptions,
              placeholder: "All Categories",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />

          <SimpleItem
            dataField="report_parameters.users"
            label={{ text: "Users" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              displayExpr: "full_name",
              items: userOptions,
              placeholder: "All Users",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
              itemTemplate: "report_parameters.users",
            }}
          />
          <Template
            name="report_parameters.users"
            render={(data) => <UserSelectBoxItem data={data} />}
          />
        </Form>
      </CollapseSection>
    </>
  );
};

export default TimeEntriesParamsForm;
